import { Injectable } from '@angular/core';
import { HttpClient, HttpResponse } from '@angular/common/http';
import { Observable } from 'rxjs';
import { ApiConfiguration } from '@/core/api/api-configuration';
import { BaseService } from '@/core/api/base-service';
import { environment } from '@env/environment';
import { SICOM_PATHS } from 'src/sicom-paths';
import { RelatorioSelecaoExportRequest, RelatorioSelecaoExportResponse, RelatorioSelecaoJobCreatedResponse, RelatorioSelecaoLoteExportRequest, RelatorioSelecaoPesquisaResponse } from './relatorio-selecao.model';

/**
 * Acesso HTTP ao endpoint de relatório por seleção de médicos.
 * Expõe pesquisa de médicos e o fluxo de exportação PDF (síncrono/assíncrono) por médico.
 */
@Injectable({ providedIn: 'root' })
export class RelatorioSelecaoService extends BaseService {
    readonly PATH = SICOM_PATHS.registro.medico.reports.relatorioSelecao;

    constructor(config: ApiConfiguration, http: HttpClient) {
        super(config, http, environment.apis.apiSicom);
    }

    /**
     * Pesquisa médicos por filtros dinâmicos (nome, CRM, UF, CPF, situação, sexo, jubilado, PCD).
     * `truncado=true` na resposta indica que a busca bateu no limite de 200 registros.
     */
    pesquisar(params: Record<string, string>): Observable<RelatorioSelecaoPesquisaResponse> {
        return this.http.get<RelatorioSelecaoPesquisaResponse>(`${this.PATH}/pesquisar`, { params });
    }

    /**
     * Dispara a exportação PDF para um médico (endpoint aceita um por chamada):
     * retorna 200 com PDF em base64 (síncrono) ou 202 com jobId (assíncrono via polling).
     */
    exportarPdf(request: RelatorioSelecaoExportRequest): Observable<HttpResponse<RelatorioSelecaoExportResponse | RelatorioSelecaoJobCreatedResponse>> {
        return this.http.post<RelatorioSelecaoExportResponse | RelatorioSelecaoJobCreatedResponse>(`${this.PATH}/exportar-pdf`, request, { observe: 'response' });
    }

    /**
     * Dispara a exportação de um único PDF combinando todos os médicos informados
     * (formato de tabela do legado): retorna 200 com PDF em base64 (síncrono)
     * ou 202 com jobId (assíncrono via polling).
     */
    exportarPdfLote(request: RelatorioSelecaoLoteExportRequest): Observable<HttpResponse<RelatorioSelecaoExportResponse | RelatorioSelecaoJobCreatedResponse>> {
        return this.http.post<RelatorioSelecaoExportResponse | RelatorioSelecaoJobCreatedResponse>(`${this.PATH}/exportar-pdf-lote`, request, { observe: 'response' });
    }

    /**
     * Exporta o CSV do lote a partir dos mesmos dados usados no PDF (todos os campos, não só
     * os trazidos pela busca) — evita a divergência do CSV montado no cliente.
     */
    exportarCsvLote(request: RelatorioSelecaoLoteExportRequest): Observable<Blob> {
        return this.http.post(`${this.PATH}/exportar-csv-lote`, request, { responseType: 'blob' });
    }
}
